import { Check } from 'lucide-react'
import { useState } from 'react'

import {
  useChatMessages,
  useChatMutations,
  useChatSending,
} from '@/modules/chat/hooks'
import type { ChatMessage } from '@/modules/chat/types'
import { isToolResultMessage } from '@/modules/chat/utils/extractArtifacts'
import { useAsyncError } from '@/modules/errors'
import { cn } from '@/utils'

export interface ChoicesSpec {
  question: string
  options: string[]
}

interface ChoicesArtifactProps {
  slug: string
  spec: ChoicesSpec
  toolUseId: string
}

export default function ChoicesArtifact({
  slug,
  spec,
  toolUseId,
}: ChoicesArtifactProps) {
  const messages = useChatMessages()
  const { sendMessage } = useChatMutations()
  const sendingMessage = useChatSending()
  const throwAsyncError = useAsyncError()
  const [picked, setPicked] = useState<string | null>(null)

  function pick(option: string) {
    setPicked(option)
    sendMessage(slug, option).catch((error: Error) => {
      setPicked(null)
      throwAsyncError(error)
    })
  }

  const answer = picked ?? answerAfter(messages, toolUseId)
  const locked = answer !== null || sendingMessage

  return (
    <figure className="my-1 w-full overflow-hidden rounded-xl border bg-card shadow-sm">
      <figcaption className="border-b px-4 py-2.5 text-sm font-medium text-foreground">
        {spec.question}
      </figcaption>
      <div className="py-1">
        {spec.options.map((option) => {
          const chosen = option === answer
          return (
            <button
              key={option}
              type="button"
              disabled={locked}
              onClick={() => pick(option)}
              className={cn(
                'flex w-full cursor-pointer items-start gap-3 px-4 py-2 text-left text-sm transition-colors enabled:hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none disabled:cursor-default',
                chosen && 'bg-emerald-50 font-medium text-foreground',
                answer !== null && !chosen && 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  chosen
                    ? 'border-emerald-600 bg-emerald-600 text-white'
                    : 'border-muted-foreground/40',
                )}
              >
                {chosen && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span className="flex-1">{option}</span>
            </button>
          )
        })}
      </div>
    </figure>
  )
}

function answerAfter(
  messages: ChatMessage[],
  toolUseId: string,
): string | null {
  const askedAt = messages.findIndex((message) =>
    message.contentJson.some(
      (block) => block.type === 'tool_use' && block.id === toolUseId,
    ),
  )
  if (askedAt === -1) return null
  const reply = messages
    .slice(askedAt + 1)
    .find((message) => message.role === 'user' && !isToolResultMessage(message))
  return reply ? (reply.contentText ?? '') : null
}
